import { StaticAsset } from '@core/models/assets.models'
import { getStaticItem, parseStatic } from '@core/utils/assets.utils'
import { Competition, MatchDayData, Season } from '@core/models/season-competition.models'
import { get, orderBy } from 'lodash-es'
import { StaticOrientation } from '@core/enums/statics.enum'

export const mapCompetition = (competition: Competition, season?: Season): Competition => ({
  ...competition,
  logo: parseStatic(competition?.statics, StaticOrientation.Landscape),
  portraitLogo: parseStatic(competition?.statics, StaticOrientation.Portrait),
  ...(season ? { season, seasonId: season.id } : {})
})

export const compLogoPredicate =
  (orientation = StaticOrientation.Portrait, size = 'Medium', isColor = true) =>
  (staticItem: StaticAsset): boolean =>
    staticItem.orientation === orientation && staticItem.size === size && staticItem.isColor === isColor

export const parseCompetitionStatics = (statics: StaticAsset[]) => ({
  portraitLogo: getStaticItem(statics, compLogoPredicate()),
  portraitLogoWhite: getStaticItem(statics, compLogoPredicate(StaticOrientation.Portrait, 'High', false)),
  landscapeLogo: getStaticItem(statics, compLogoPredicate(StaticOrientation.Landscape, 'Small')),
  landscapeLogoWhite: getStaticItem(statics, compLogoPredicate(StaticOrientation.Landscape, 'High2x', false)),
  isoTypeLogo: getStaticItem(statics, compLogoPredicate(StaticOrientation.Portrait, 'Small'))
})

export const beautifySeasonName = (officialName: string): string => {
  return officialName.replace(/(\d{4})\s*-\s*(\d{4})/, (_match, year1, year2) => {
    return `${year1}/${year2.substring(2)}`
  })
}

export const findSeasonById = (seasons: Season[], seasonId: string) => {
  return (seasons || []).find((s) => s.id === seasonId)
}

export const findSeasonAndCompetitionByID = (
  seasons: Season[],
  seasonId: string,
  competitionId: string
): {
  season: Season
  competition: Competition
} => {
  const season = findSeasonById(seasons, seasonId)
  const competition = (season?.competitions || []).find((competition) => competition.id === competitionId)

  return { season, competition }
}

export const findSeasonCompetitionByCompetitionID = (
  seasons: Season[],
  competitionId: string
): { season: Season; competition: Competition } | undefined => {
  for (let i = 0; i < seasons?.length; i++) {
    const competition = seasons[i].competitions.find((s) => s.id === competitionId)
    if (competition) {
      return {
        season: seasons[i],
        competition
      }
    }
  }

  return undefined
}

const mapSeasonCompetition = (competition: Competition, seasonId: string): Competition => {
  const matchdays = get(competition, 'matchdays.items', [])

  const currentMatchDay = matchdays.find((md) => md.selected)

  return {
    ...competition,
    name: competition.officialName,
    seasonId,
    currentMatchDayNumber: currentMatchDay?.number,
    matchdays: orderBy(matchdays, ['number'], ['desc']) as MatchDayData[],
    ...parseCompetitionStatics(competition.statics)
  }
}

const mapSeason = (season: Season): Season => ({
  ...season,
  name: season.officialName,
  beautifiedName: beautifySeasonName(season.officialName),
  order: parseInt(season.officialName, 10) || 0,
  competitions: season.competitions.map((comp) => mapSeasonCompetition(comp, season.id))
})

export const mapSeasons = ({ seasons }: { seasons: Season[] }): Season[] => {
  const seasonList: Season[] = seasons.map(mapSeason)

  return orderBy(seasonList, ['order'], ['desc'])
}

export const hasNoCompetitions = (seasons: Season[]): boolean =>
  !seasons?.some((season) => season.competitions?.length)

export const findCompetitionInSeason = (season: Season, competitionId: string): Competition => {
  return season?.competitions?.find((cmp) => cmp.id === competitionId)
}

export const findSeasonAndCompetitionWithFallback = (
  seasons: Season[],
  seasonId: string,
  competitionId: string,
  selectedCompetitionId: string
): { season: Season; competition: Competition } => {
  const desiredSeason = seasons?.find((s) => s.id === seasonId)
  const desiredCompetitionId =
    selectedCompetitionId ??
    competitionId ??
    desiredSeason?.competitions?.[0]?.id ??
    seasons?.[0]?.competitions?.[0]?.id

  if (desiredSeason) {
    const cmp = findCompetitionInSeason(desiredSeason, desiredCompetitionId)
    if (cmp) {
      return { season: desiredSeason, competition: cmp }
    }
  }

  for (let i = 0; i < seasons?.length; i++) {
    const competition = findCompetitionInSeason(seasons[i], desiredCompetitionId)
    if (competition) {
      return { season: seasons[i], competition }
    }
  }

  return {
    season: seasons[0],
    competition: seasons?.[0]?.competitions?.[0]
  }
}
